import React, { useState, useEffect } from 'react';
import { Box, Tooltip, IconButton, Badge } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterPanel from './FilterPanel'; // Correct path within controls/
import { mapConfig } from '../../../config/mapConfig'; // Corrected path
// Import authenticated API services
import { organizationsApi, classificationApi, ratingApi, default as api } from '../../../services/api';
// Import user context for auto-selection
import { useAuth } from '../../../context/AuthContext';

const FilterManager = ({
  // Props from parent (MapViewPage)
  onFilterChange, // Callback to pass selected filters up
  assets, // Receive the filtered assets array
  displayedCount, // Current number of displayed assets
  // Layer visibility props (passed down to FilterPanel)
  showRoadLines,
  setShowRoadLines,
  showIoTSensors,
  setShowIoTSensors,
  showAssetFootprints,
  setShowAssetFootprints,
  closeSignal
  // Removed 3D model props
}) => {
  const theme = useTheme();
  // Get user context for auto-selection and discipline filtering
  const { user, isAuthenticated, isLoading, hasDiscipline } = useAuth();

  // Fix user data access - remove double nesting
  const isSystemAdmin = user?.role === 'admin' && user?.organizationId === null;
  const userOrganizationId = user?.organizationId;

  // State variables
  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);

  // Close filter panel when closeSignal changes (e.g., when inspection panel opens)
  useEffect(() => {
    if (closeSignal > 0) {
      setIsFilterPanelVisible(false);
    }
  }, [closeSignal]);
  const [organizationsList, setOrganizationsList] = useState([]);
  // Store all networks/sections for the selected organization
  const [_allNetworksForOrg, set_allNetworksForOrg] = useState([]);
  const [_allSectionsForOrg, set_allSectionsForOrg] = useState([]);
  // Lists for display in dropdowns
  const [displayableNetworks, setDisplayableNetworks] = useState([]);
  const [displayableRegions, setDisplayableRegions] = useState([]);
  const [displayableSections, setDisplayableSections] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [typesList, setTypesList] = useState([]);
  const [subtypesList, setSubtypesList] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  // Changed to arrays for multi-select
  const [selectedNetworkIds, setSelectedNetworkIds] = useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [selectedSubtypeId, setSelectedSubtypeId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [totalAssetCount, setTotalAssetCount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Rating filter state
  const [ratingLevels, setRatingLevels] = useState([]);
  const [selectedRatingLevelId, setSelectedRatingLevelId] = useState('');
  // Slope type filter state (C=Cut, E=Embankment) - only for Slope type assets
  const [selectedSlopeType, setSelectedSlopeType] = useState('');
  // Critical asset filter state
  const [selectedIsCritical, setSelectedIsCritical] = useState('');

  // Handlers
  const toggleFilterPanel = () => {
    setIsFilterPanelVisible(prev => !prev);
  };

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganizationId(organizationId);
    // When organization changes, reset all location filters
    setSelectedNetworkIds([]);
    setSelectedSectionIds([]);
    setSelectedRegions([]);
    // Also reset rating since it depends on organization
    setSelectedRatingLevelId('');
    setRatingLevels([]);
  };
  // Multi-select handler for networks
  const handleNetworkChange = (networkIds) => {
    setSelectedNetworkIds(Array.isArray(networkIds) ? networkIds : []);
    // When network changes, reset sections
    setSelectedSectionIds([]);
  };
  // Multi-select handler for sections
  const handleSectionChange = (sectionIds) => {
    setSelectedSectionIds(Array.isArray(sectionIds) ? sectionIds : []);
  };
  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    // When category changes, reset the type, subtype, and rating
    setSelectedTypeId('');
    setSelectedSubtypeId('');
    setSelectedRatingLevelId('');
    setRatingLevels([]);
  };
  const handleTypeChange = (typeId) => {
    console.log("FilterManager: Type filter selected - ID:", typeId);
    setSelectedTypeId(typeId);
    // When type changes, reset the subtype, rating, and slope type
    setSelectedSubtypeId('');
    setSelectedRatingLevelId('');
    setSelectedSlopeType(''); // Reset slope type when type changes
  };
  const handleSlopeTypeChange = (value) => {
    console.log('FilterManager: Slope type changed to:', value);
    setSelectedSlopeType(value);
  };
  const handleSubtypeChange = (id) => {
    setSelectedSubtypeId(id);
  };
  // Multi-select handler for regions
  const handleRegionChange = (regions) => {
    setSelectedRegions(Array.isArray(regions) ? regions : []);
  };
  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };
  const handleRatingChange = (value) => {
    console.log('FilterManager: Rating changed to:', value, 'type:', typeof value);
    setSelectedRatingLevelId(value);
  };
  const handleIsCriticalChange = (value) => {
    setSelectedIsCritical(value);
  };
  const handleResetFilters = () => {
    // Reset all filters except organization for non-admin users
    if (!isSystemAdmin && userOrganizationId) {
      setSelectedOrganizationId(userOrganizationId); // Keep organization selected for non-admin
    } else {
      setSelectedOrganizationId(''); // Allow clearing for admin
    }

    // Reset multi-select arrays
    setSelectedNetworkIds([]);
    setSelectedSectionIds([]);
    setSelectedRegions([]);
    setSelectedCategoryId('');
    setSelectedTypeId('');
    setSelectedSubtypeId('');
    setSelectedStatus('');
    setSearchTerm('');
    setSelectedRatingLevelId('');
    setRatingLevels([]);
    setSelectedSlopeType(''); // Reset slope type
    setSelectedIsCritical(''); // Reset critical filter

    // Send reset filters to parent
    onFilterChange({
      organization_id: !isSystemAdmin && userOrganizationId ? userOrganizationId : '',
      network_ids: [],
      section_ids: [],
      regions: [],
      category_id: '',
      type_id: '',
      subtype_id: '',
      searchTerm: '',
      status: '',
      rating_level_id: '',
      slope_type: '',
      is_critical: ''
    });
  };

  // Effect to call onFilterChange when any selected filter or search term changes
  useEffect(() => {
    if (typeof onFilterChange === 'function') {
      // Pass filter values with arrays for multi-select fields
      const filters = {
        organization_id: selectedOrganizationId || '',
        network_ids: selectedNetworkIds,
        section_ids: selectedSectionIds,
        regions: selectedRegions,
        category_id: selectedCategoryId || '',
        type_id: selectedTypeId || '',
        subtype_id: selectedSubtypeId || '',
        searchTerm: searchTerm || '',
        status: selectedStatus || '',
        rating_level_id: selectedRatingLevelId || '',
        slope_type: selectedSlopeType || '',
        is_critical: selectedIsCritical || ''
      };
      console.log('FilterManager: Calling onFilterChange with filters:', filters);
      onFilterChange(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrganizationId,
    selectedNetworkIds,
    selectedSectionIds,
    selectedRegions,
    selectedCategoryId,
    selectedTypeId,
    selectedSubtypeId,
    searchTerm,
    selectedStatus,
    selectedRatingLevelId,
    selectedSlopeType,
    selectedIsCritical
  ]);

// Fetch initial filter options on mount
useEffect(() => {
  // Use authenticated API calls for each endpoint
  const fetchInitialData = async () => {
    try {
      // Fetch organizations using authenticated API
      const orgResponse = await organizationsApi.getAll();
      setOrganizationsList(orgResponse.data);
      
      // Auto-select user's organization for non-admin users
      if (!isSystemAdmin && userOrganizationId && orgResponse.data.length > 0) {
        console.log('Auto-selection check - isSystemAdmin:', isSystemAdmin);
        console.log('Auto-selection check - userOrganization:', userOrganizationId);
        console.log('Auto-selection check - organizations fetched:', orgResponse.data);
        
        const userOrg = orgResponse.data.find(org => org.id === userOrganizationId);
        console.log('Auto-selection check - found userOrg:', userOrg);
        
        if (userOrg) {
          console.log('Auto-selecting organization:', userOrg.name, 'ID:', userOrganizationId);
          setSelectedOrganizationId(userOrganizationId);
          
          // For network/section users, also auto-select their accessible networks
          if (user?.permissionScope === 'network_section') {
            try {
              console.log('Network/section user detected - fetching user permissions...');
              const permissionsResponse = await api.get(`/auth/me`);
              const userPermissions = permissionsResponse.data.permissions || [];
              
              console.log('User permissions fetched:', userPermissions);
              
              // Look for network-level permissions (these give access to entire networks)
              const networkPermissions = userPermissions.filter(p => p.network_id && !p.section_id);
              
              if (networkPermissions.length > 0) {
                // Auto-select the first accessible network (as single-item array)
                const firstNetworkId = networkPermissions[0].network_id;
                console.log('Auto-selecting first accessible network:', firstNetworkId);
                setSelectedNetworkIds([firstNetworkId]);
              } else {
                // Look for section-level permissions and auto-select the first network that contains accessible sections
                const sectionPermissions = userPermissions.filter(p => p.section_id);
                if (sectionPermissions.length > 0) {
                  // Get unique network IDs from section permissions
                  const networkIds = [...new Set(sectionPermissions.map(p => p.network_id).filter(Boolean))];
                  if (networkIds.length > 0) {
                    console.log('Auto-selecting network from section permissions:', networkIds[0]);
                    setSelectedNetworkIds([networkIds[0]]);
                  }
                }
              }
            } catch (permError) {
              console.error('Error fetching user permissions for auto-selection:', permError);
            }
          }
        } else {
          console.log('ERROR: User organization not found in organizations list');
          console.log('Looking for organization ID:', userOrganizationId);
          console.log('Available organizations:', orgResponse.data.map(org => ({ id: org.id, name: org.name })));
        }
      } else {
        console.log('Auto-selection skipped - isSystemAdmin:', isSystemAdmin, 'userOrganization:', userOrganizationId, 'orgs length:', orgResponse.data.length);
      }
      
      // Fetch categories using authenticated API and filter by user's disciplines
      const categoriesResponse = await classificationApi.getCategories();
      const allCategories = categoriesResponse.data;

      // Filter categories by user's disciplines (unless system admin)
      const accessibleCategories = isSystemAdmin
        ? allCategories
        : allCategories.filter(cat => hasDiscipline(cat.name));

      console.log('FilterManager: Categories filtered by discipline:', {
        total: allCategories.length,
        accessible: accessibleCategories.length,
        isSystemAdmin
      });

      setCategoriesList(accessibleCategories);
      
      // Fetch types using authenticated API
      const typesResponse = await classificationApi.getTypes();
      setTypesList(typesResponse.data);
      
      // Fetch subtypes using authenticated API
      const subtypesResponse = await classificationApi.getSubtypes();
      setSubtypesList(subtypesResponse.data);
    } catch (error) {
      console.error('Error fetching initial filter data:', error);
      // Set empty arrays on error
      setOrganizationsList([]);
      setCategoriesList([]);
      setTypesList([]);
      setSubtypesList([]);
    }
  };
  
  fetchInitialData();
  
  const fetchTotalCount = async () => {
    try {
      // Use the imported authenticated api instance for custom endpoints
      const response = await api.get('/assets/count');
      setTotalAssetCount(response.data.count);
    } catch (error) {
      console.error("Error fetching total asset count:", error);
      setTotalAssetCount(null);
    }
  };
  
  fetchTotalCount();
}, [isSystemAdmin, userOrganizationId, user?.permissionScope, hasDiscipline]); // Add hasDiscipline to dependencies for discipline filtering

// Fetch Networks for the selected Organization
useEffect(() => {
  if (selectedOrganizationId) {
    const fetchNetworks = async () => {
      try {
        // Use the imported authenticated api instance
        const response = await api.get(`/networks?organization_id=${selectedOrganizationId}`);
        set_allNetworksForOrg(response.data);
      } catch (error) {
        console.error('Error fetching networks:', error);
        set_allNetworksForOrg([]);
      }
    };
    fetchNetworks();
  } else {
    set_allNetworksForOrg([]); // Clear if no organization is selected
  }
  // Reset dependent filters (now arrays)
  setSelectedNetworkIds([]);
  setSelectedSectionIds([]);
}, [selectedOrganizationId]);

// Fetch all Sections for the networks of the selected Organization
useEffect(() => {
  if (selectedOrganizationId) {
    const fetchAllSectionsForOrg = async () => {
      try {
        // Use the imported authenticated api instance
        const response = await api.get(`/sections?organization_id=${selectedOrganizationId}`);
        set_allSectionsForOrg(response.data || []);
      } catch (error) {
        console.error("Error fetching sections for organization:", error);
        set_allSectionsForOrg([]);
      }
    };
    fetchAllSectionsForOrg();
  } else {
    set_allSectionsForOrg([]); // Clear if no org selected
  }
  setSelectedSectionIds([]); // Reset sections (array) when org's sections change
}, [selectedOrganizationId]); // Remove _allNetworksForOrg dependency

// Derive displayableRegions from _allSectionsForOrg
useEffect(() => {
  if (_allSectionsForOrg.length > 0) {
    const regions = [...new Set(_allSectionsForOrg.map(section => section.region).filter(Boolean))];
    setDisplayableRegions(regions.sort());
  } else {
    setDisplayableRegions([]);
  }
}, [_allSectionsForOrg]);

// Derive displayableNetworks based on _allNetworksForOrg and selectedRegions (multi-select)
useEffect(() => {
  if (selectedOrganizationId) {
    if (selectedRegions.length > 0 && _allSectionsForOrg.length > 0) {
      // Filter networks that have sections in any of the selected regions
      const networkIdsInRegions = [
        ...new Set(
          _allSectionsForOrg
            .filter(section => selectedRegions.includes(section.region))
            .map(section => section.network_id)
        ),
      ];
      setDisplayableNetworks(
        _allNetworksForOrg.filter(network => networkIdsInRegions.includes(network.id))
      );
    } else {
      // If no regions selected, show all networks for the organization
      setDisplayableNetworks(_allNetworksForOrg);
    }
  } else {
    setDisplayableNetworks([]);
  }
}, [_allNetworksForOrg, _allSectionsForOrg, selectedRegions, selectedOrganizationId]);

// Derive displayableSections based on _allSectionsForOrg, selectedNetworkIds, and selectedRegions (multi-select)
useEffect(() => {
  let filteredSections = _allSectionsForOrg;

  // Filter by selected regions (if any selected)
  if (selectedRegions.length > 0) {
    filteredSections = filteredSections.filter(
      (section) => selectedRegions.includes(section.region)
    );
  }

  // Filter by selected networks (if any selected)
  if (selectedNetworkIds.length > 0) {
    filteredSections = filteredSections.filter(
      (section) => selectedNetworkIds.includes(section.network_id)
    );
  }

  setDisplayableSections(filteredSections);
}, [_allSectionsForOrg, selectedNetworkIds, selectedRegions]);

// Fetch Types when Category changes
useEffect(() => {
  const fetchTypes = async () => {
    try {
      if (selectedCategoryId) {
        // Fetch types filtered by category
        const response = await classificationApi.getTypes({ category_id: selectedCategoryId });
        setTypesList(response.data);
      } else {
        // Fetch all types
        const response = await classificationApi.getTypes();
        setTypesList(response.data);
      }
    } catch (error) {
      console.error('Error fetching types:', error);
      setTypesList([]);
    }
  };
  
  fetchTypes();
  // Reset is handled in handleCategoryChange
}, [selectedCategoryId]);

// Fetch Subtypes when Type changes (not Category)
useEffect(() => {
  const fetchSubtypes = async () => {
    try {
      if (selectedTypeId) {
        // Fetch subtypes filtered by type
        const response = await classificationApi.getSubtypes({ type_id: selectedTypeId });
        setSubtypesList(response.data);
      } else {
        // Fetch all subtypes
        const response = await classificationApi.getSubtypes();
        setSubtypesList(response.data);
      }
    } catch (error) {
      console.error('Error fetching subtypes:', error);
      setSubtypesList([]);
    }
  };

  fetchSubtypes();
  // Reset is handled in handleTypeChange
}, [selectedTypeId]);

// Fetch Rating Levels when Type changes (rating schemes are per asset type)
useEffect(() => {
  const fetchRatingLevels = async () => {
    // Need both organization and type to fetch rating scheme
    const effectiveOrgId = selectedOrganizationId || userOrganizationId;
    console.log('Fetching rating levels - effectiveOrgId:', effectiveOrgId, 'selectedTypeId:', selectedTypeId);

    if (selectedTypeId && effectiveOrgId) {
      try {
        // First fetch the rating scheme for this org + type
        const schemeResponse = await ratingApi.getSchemes({
          organization_id: effectiveOrgId,
          asset_type_id: selectedTypeId
        });
        console.log('Rating scheme response:', schemeResponse.data);

        // Backend returns { data: schemes }, so access schemeResponse.data.data
        const schemes = schemeResponse.data?.data || schemeResponse.data || [];

        if (Array.isArray(schemes) && schemes.length > 0) {
          const schemeId = schemes[0].id;
          console.log('Found rating scheme, fetching levels for schemeId:', schemeId);

          // Fetch levels for this scheme
          const levelsResponse = await ratingApi.getLevels({ scheme_id: schemeId });
          console.log('Rating levels response:', levelsResponse.data);

          // Backend returns { data: levels }
          const levels = levelsResponse.data?.data || levelsResponse.data || [];
          setRatingLevels(Array.isArray(levels) ? levels : []);
        } else {
          console.log('No rating scheme found for this type');
          setRatingLevels([]);
        }
      } catch (error) {
        console.error('Error fetching rating levels:', error);
        setRatingLevels([]);
      }
    } else {
      setRatingLevels([]);
    }
  };

  fetchRatingLevels();
  // Reset rating selection when type changes
  setSelectedRatingLevelId('');
}, [selectedTypeId, selectedOrganizationId, userOrganizationId]);

const collectCurrentFilters = () => {
  return {
    organization_id: selectedOrganizationId,
    network_ids: selectedNetworkIds,
    section_ids: selectedSectionIds,
    regions: selectedRegions,
    category_id: selectedCategoryId,
    type_id: selectedTypeId,
    subtype_id: selectedSubtypeId,
    searchTerm: searchTerm,
    status: selectedStatus,
    rating_level_id: selectedRatingLevelId,
    slope_type: selectedSlopeType
  };
};

  return (
    <>
      {/* Filter Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 10,
        }}
      >
        <Tooltip
          title={isFilterPanelVisible ? "Hide Filters" : "Show Filters"}
          placement="right"
        >
          <Badge
            variant="dot"
            color="error"
            invisible={!(selectedOrganizationId || selectedNetworkIds.length > 0 || selectedSectionIds.length > 0 || selectedRegions.length > 0 || selectedCategoryId || selectedTypeId || selectedSubtypeId || searchTerm || selectedStatus || selectedRatingLevelId || selectedSlopeType)}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                top: 6,
                right: 6,
              }
            }}
          >
            <IconButton
              onClick={toggleFilterPanel}
              size="large"
              sx={{
                backgroundColor: isFilterPanelVisible
                  ? alpha(theme.palette.primary.main, 0.9)
                  : alpha(theme.palette.background.paper, 0.85),
                color: isFilterPanelVisible ? 'white' : 'inherit',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isFilterPanelVisible
                    ? alpha(theme.palette.primary.main, 1)
                    : alpha(theme.palette.background.paper, 0.95),
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
              }}
            >
              <FilterListIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Badge>
        </Tooltip>
      </Box>

      {/* Render the Filter Panel - only when visible */}
      {isFilterPanelVisible && (
        <FilterPanel
          organizations={organizationsList}
          networks={displayableNetworks}
          sections={displayableSections}
          regions={displayableRegions}
          categories={categoriesList}
          types={typesList}
          subtypes={subtypesList}
          ratingLevels={ratingLevels}
          assets={assets}
          isSystemAdmin={isSystemAdmin}
          selectedOrganizationId={selectedOrganizationId}
          selectedNetworkIds={selectedNetworkIds}
          selectedSectionIds={selectedSectionIds}
          selectedRegions={selectedRegions}
          selectedCategoryId={selectedCategoryId}
          selectedTypeId={selectedTypeId}
          selectedSubtypeId={selectedSubtypeId}
          selectedStatus={selectedStatus}
          selectedRatingLevelId={selectedRatingLevelId}
          selectedSlopeType={selectedSlopeType}
          selectedIsCritical={selectedIsCritical}
          onOrganizationChange={handleOrganizationChange}
          onNetworkChange={handleNetworkChange}
          onSectionChange={handleSectionChange}
          onRegionChange={handleRegionChange}
          onCategoryChange={handleCategoryChange}
          onTypeChange={handleTypeChange}
          onSubtypeChange={handleSubtypeChange}
          onStatusChange={handleStatusChange}
          onRatingChange={handleRatingChange}
          onSlopeTypeChange={handleSlopeTypeChange}
          onIsCriticalChange={handleIsCriticalChange}
          onResetFilters={handleResetFilters}
          onSearchTermChange={setSearchTerm}
          displayedCount={displayedCount}
          totalCount={totalAssetCount}
          showRoadLines={showRoadLines}
          setShowRoadLines={setShowRoadLines}
          showIoTSensors={showIoTSensors}
          setShowIoTSensors={setShowIoTSensors}
          showAssetFootprints={showAssetFootprints}
          setShowAssetFootprints={setShowAssetFootprints}
          searchTerm={searchTerm}
        />
      )}
    </>
  );
};

export default FilterManager;